import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Certificate } from '@adornis/buchungsheld-db/entities/certificate.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { Suspension } from '@adornis/buchungsheld-db/entities/suspension.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { dataviewEmptyState } from '@adornis/buchungsheld-db/global-helpers.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-data-view-table.js';
import '@adornis/buchungsheld-theme/components/tr-data-view.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import { bhPromptConfirmParticipationRemove } from '@adornis/buchungsheld-theme/prompts/bh-prompt-confirm.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-loading-state.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/cms/client/generated-form.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { signalToObservable } from '@adornis/functional-lit/signal-to-observable.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, map, of, switchMap } from 'rxjs';
import '../../components/bh-stammdaten-form';
import { BHPromptSuspendUser } from '../../prompts/bh-prompt-suspend-user.js';
import { adminEinrichtungUserRouter } from './bh-page-admin-einrichtung-users.js';

/**
 * @element bh-page-admin-einrichtung-user-detail
 */
@customElement('bh-page-admin-einrichtung-user-detail')
export class BHPageAdminEinrichtungUserDetail extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @state() user = new RXController(this, signalToObservable(adminEinrichtungUserRouter.variables), params$ =>
    params$.pipe(
      map(params => params.userID),
      switchMap(userID =>
        !userID
          ? of(null)
          : subscribeByID(
              User,
              userID,
            )({
              _id: 1,
              createdAt: 1,
              email: 1,
              firstName: 1,
              lastName: 1,
              participations: {
                _id: 1,
                _class: 1,
                state: 1,
                createdAt: 1,
                institutionID: 1,
                userID: 1,
                product: {
                  _id: 1,
                  _class: 1,
                  name: 1,
                  start: 1,
                  end: 1,
                  appointments: {
                    _id: 1,
                    start: 1,
                    end: 1,
                  },
                  slotConfig: {
                    start: 1,
                    end: 1,
                  },
                },
              },
              suspentions: Suspension.allFields,
            }),
      ),
    ),
  );

  @state() private readonly _stammdaten = new RXController(
    this,
    combineLatest([
      this.user.observable.pipe(filter(Boolean)),
      this.institutionID.observable.pipe(filter(Boolean)),
    ]).pipe(
      filter(Boolean),
      switchMap(([user, institutionID]) =>
        UserStammdaten.getByUserAndInstitution<UserStammdaten>(user._id, institutionID)(UserStammdaten.allFields),
      ),
    ),
  );

  @state() private readonly userController = new GenericFieldFormController(this, this.user.observable, {
    success: doc => {
      return doc;
    },
    error: error => XSnackbar.show(`${this._translation.translate('error')}: ${error.message}`),
    disabledFields: { createdAt: 1, email: 1 },
  });

  @state() private readonly stammdatenController = new GenericFieldFormController(
    this,
    this._stammdaten.observable.pipe(filter(Boolean)),
    {
      success: doc => {
        return doc;
      },
      error: err => {
        console.log(err);
        return null;
      },
    },
  );

  // eslint-disable-next-line max-lines-per-function
  override render() {
    if (this.user.isLoading) return html` <x-loading-state></x-loading-state> `;
    if (!this.userController.document || !this.stammdatenController.document)
      return html`
        <x-empty-placeholder
          .text=${this._translation.translate('user', 'not_found')}
          .img=${'close'}
          no-wrapper
        ></x-empty-placeholder>
      `;

    const invoiceMngmtPlugin = getPluginForScope('invoice-management', this.institutionID.value!);
    return html`
      <tr-flex space="md">
        <bh-breadcrumb
          .items=${[
            {
              content: this._translation.translate('counseling', 'participant'),
              active: false,
              href: `/admin/institution-details/${this.institutionID.value}/users`,
            },
            { content: this._translation.translate('user', 'details'), active: true },
          ]}
          .additionalAction=${html`
            <x-flex horizontal space="md">
              <tr-button-small
                icon="send"
                mode="outline"
                .action=${() => {
                  const email = this.user.value?.email;
                  if (!email) return;
                  goTo(`mailto:${email}`, { target: '_blank' });
                }}
              >
                <tr-h4> ${this._translation.translate('user', 'write_mail')} </tr-h4>
              </tr-button-small>
              <tr-button-small icon="delete" tone="error" .action=${async () => {}}>
                <tr-h4 bold> ${this._translation.translate('delete')} </tr-h4>
              </tr-button-small>
              <tr-button-small
                icon="save"
                .action=${async () => {
                  if (!this.userController.document || !this.stammdatenController.document) return;

                  this.userController.document.participations = [];
                  const user = await this.userController.validate();
                  const stammdaten = await this.stammdatenController.validate();

                  if (user && stammdaten) {
                    const allUpdateCmds = await Promise.all([
                      user.save().catch((err: Error) => XSnackbar.show(`Error: ${err.message}`)),
                      stammdaten.save().catch((err: Error) => XSnackbar.show(`Error: ${err.message}`)),
                    ]);

                    const updateSum = allUpdateCmds.reduce<number>((sum, res) => sum + (res ?? 0), 0);
                    if (updateSum === allUpdateCmds.length)
                      return goTo(`/admin/institution-details/${this.institutionID.value}/users`);
                    else XSnackbar.show(`Leider konnten die Nutzerdaten nicht gespeichert werden!`);
                  }
                }}
              >
                <tr-h4 bold> ${this._translation.translate('save')} </tr-h4>
              </tr-button-small>
            </x-flex>
          `}
        ></bh-breadcrumb>

        <x-flex space="lg">
          <tr-flex space="sm">
            <generated-form
              placeholder-mode="static-floating"
              .formController=${this.userController}
              .whitelist=${['firstName', 'lastName', 'email', 'createdAt']}
            ></generated-form>

            <bh-stammdaten-form
              .institutionID=${this.institutionID.value}
              .user=${this.userController.document}
              .stammdaten=${this._stammdaten.observable}
              .stammdatenController=${this.stammdatenController}
            ></bh-stammdaten-form>
          </tr-flex>

          <x-hr></x-hr>

          <x-flex space="md">
            <tr-h3> Zertifikate für folgende Semester </tr-h3>
            <tr-data-view
              .coloring=${this.colors.tone.light_orange}
              .options=${{
                class: Certificate,
                emptyState: () => dataviewEmptyState({}),
                selectionSet: { file: selectionSet(() => AdornisFile, 2), title: 1, createdAt: 1, fileID: 1, text: 1 },
                view: new View({
                  filter: new BHFilter({ userID: this.user.value?._id }),
                }),
                customColumns: [
                  { fieldName: 'title' },
                  {
                    columnName: 'Datei',
                    fieldName: 'file',
                    selectionSet: { ...AdornisFile.allFields, fileID: 1, file: AdornisFile.allFields },
                    renderItem: instance => {
                      const text = instance.file
                        ? instance.file.meta?.fileName
                        : this._translation.translate('settings', 'no_file');
                      return html` <x-text> ${text} </x-text> `;
                    },
                  },
                  {
                    fieldName: 'createdAt',
                    columnWidth: 'max-content',
                    getFieldMapping: instance => instance.createdAt.toLocaleString(DateTime.DATE_MED),
                  },
                  {
                    customID: '_options',
                    renderHeader: _ => html`
                      <x-flex
                        space="xs"
                        horizontal
                        center
                        crossaxis-center
                        w100
                        ${acss({
                          color: this.colors.tone.light_orange!.shade(800),
                          'tr-icon': { fontSize: `${this.baseFontSize}px` },
                          'tr-text': { textWrap: 'nowrap' },
                        })}
                      >
                        <tr-icon>electric_bolt</tr-icon>
                      </x-flex>
                    `,
                    renderItem: instance => {
                      return html`
                        <x-flex space="xs" wrap horizontal>
                          <tr-button-small
                            icon="visibility"
                            .color=${this.colors.tone.light_orange!.shade(600)}
                            mode="outline"
                            @click=${() => goTo(Certificate.showPreview(instance.fileID), { target: '_blank' })}
                          ></tr-button-small>
                        </x-flex>
                      `;
                    },
                    columnWidth: 'max-content',
                  },
                ] as Array<ColumnDefinition<typeof Certificate>>,
              } satisfies DataViewOptions<typeof Certificate>}
            ></tr-data-view>
          </x-flex>

          <x-hr></x-hr>

          ${this._stammdaten.value
            ? html`
                ${invoiceMngmtPlugin.render(this._stammdaten.value, this)}
                <x-hr></x-hr>
              `
            : nothing}

          <tr-flex space="md">
            <tr-h3> ${this._translation.translate('user', 'other')} </tr-h3>
            <tr-flex space="sm" horizontal crossaxis-start wrap>
              ${this.user.value?.suspentions
                .map(suspension => suspension.institutionID)
                .includes(this.institutionID.value ?? '')
                ? html`
                    <tr-button-small
                      icon="verified"
                      .color=${this.colors.tone.success}
                      .action=${async e => {
                        const user = this.user.value;
                        if (!user) return;

                        e.preventDefault();
                        e.stopPropagation();

                        if (
                          !(await XDialog.confirm(
                            this._translation.translate('user', 'share_user_with_institution') + ` (${user.email})`,
                          ))
                        )
                          return;

                        if (!this.institutionID.value) return;

                        await XDialog.waitFor(
                          User.cancelSuspensionFromInstitution(user._id, this.institutionID.value)(),
                          this._translation.translate('user', 'anulled_suspension') as string,
                        );
                        this.requestUpdate();
                      }}
                    >
                      <tr-h4> ${this._translation.translate('user', 'anull_suspension')} </tr-h4>
                    </tr-button-small>
                  `
                : html`
                    <tr-button-small
                      icon="block"
                      .color=${this.colors.tone.error}
                      .action=${async e => {
                        const user = this.user.value;
                        if (!user) return;

                        e.preventDefault();
                        e.stopPropagation();

                        await BHPromptSuspendUser.showPopup({
                          props: { user, institutionID: this.institutionID.value },
                          modal: true,
                        });
                        this.requestUpdate();
                      }}
                    >
                      <tr-h4> ${this._translation.translate('suspend')} </tr-h4>
                    </tr-button-small>
                  `}
            </tr-flex>
          </tr-flex>

          <x-hr></x-hr>

          <x-flex space="md">
            <tr-h3> ${this._translation.translate('user', 'reg_to_event')} </tr-h3>
            <tr-data-view
              .coloring=${this.colors.tone.success}
              .options=${{
                class: Participation,
                operation: 'subscribeParticipationsByUserPaginated',
                isEntryClickable: false,
                isSortable: true,
                view: new View({ filter: new BHFilter({ userID: this.user.value?._id }) }),
                customColumns: [
                  {
                    fieldName: 'product.name',
                    columnName: 'Veranstaltung',
                    selectionSet: {
                      ...selectionSet(() => User, 4),
                      product: { _id: 1, name: 1, appointments: { _id: 1, start: 1 } },
                    },
                  },
                  {
                    columnName: this._translation.translate('field', 'state'),
                    fieldName: 'state',
                    renderItem: instance => html`
                      <tr-text> ${this._translation.translate('WORKSHOPS', 'STATES', instance.state)} </tr-text>
                    `,
                  },
                  {
                    columnName: 'Beginnt am',
                    customID: 'startAt',
                    renderItem: instance => {
                      const product = instance.product;
                      const start = (() => {
                        if (!product) return null;
                        if (product._class === Workshop._class) return (product as Workshop).start;
                        if (product._class === Einzelberatung._class)
                          return (product as Einzelberatung).slotConfig?.start;
                        throw new Error('unreachable');
                      })();

                      return html` <tr-text> ${start?.toLocaleString(DateTime.DATETIME_MED) ?? '-'} </tr-text> `;
                    },
                  },
                  {
                    customID: 'viewBtn',
                    columnWidth: 'min-content',
                    renderHeader: _ => html`
                      <x-flex
                        space="xs"
                        horizontal
                        center
                        crossaxis-center
                        w100
                        ${acss({
                          color: this.colors.tone.success.shade(800),
                          'tr-icon': { fontSize: `${this.baseFontSize}px` },
                          'tr-text': { textWrap: 'nowrap' },
                        })}
                      >
                        <tr-icon>electric_bolt</tr-icon>
                        <tr-text bold>Aktionen</tr-text>
                      </x-flex>
                    `,
                    renderItem: instance => html`
                      <x-flex space="xs" horizontal>
                        <tr-button-small
                          icon="edit"
                          mode="outline"
                          .color=${this.colors.tone.success.shade(600)}
                          .action=${() =>
                            goTo(
                              `/admin/institution-details/${this.institutionID.value}/workshops/edit/${instance.product?._id}`,
                            )}
                        ></tr-button-small>

                        <tr-button-small
                          icon="more_horiz"
                          mode="outline"
                          .color=${this.colors.tone.success.shade(600)}
                          .action=${() =>
                            goTo(
                              `/admin/institution-details/${this.institutionID.value}/workshops/modify/${instance.product?._id}`,
                            )}
                        ></tr-button-small>

                        <tr-button-small
                          icon="delete"
                          mode="outline"
                          .color=${this.colors.tone.success.shade(600)}
                          .action=${async () => bhPromptConfirmParticipationRemove(async () => await instance.remove())}
                        ></tr-button-small>
                      </x-flex>
                    `,
                  },
                ],
              } satisfies DataViewOptions<typeof Participation>}
            ></tr-data-view>
          </x-flex>

          <!-- invoices -->
          ${(() => {
            const userInvoiceTablePlugin = getPluginForScope('invoice-user-invoice-table', this.institutionID.value!);
            return this.user.value?._id && this.institutionID.value
              ? userInvoiceTablePlugin.render(this, this._translation, this.user.value._id, this.institutionID.value)
              : nothing;
          })()}
        </x-flex>
      </tr-flex>
    `;
  }
}
